import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable, tap, throwError, from, switchMap, take, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { Apiservice } from './http/apiservice';
import { TokenService } from './token.service';
import { LoggerService } from './logger.service';
import { OAuth2Service, OAuth2UserData } from './oauth2/oauth2';
import { 
  AuthResponseInterface, 
  AuthResponseWithProviderInterface,
  LoginRequestInterface, 
  RegisterRequestInterface, 
  UserInterface,
  ChangePasswordRequestInterface,
  ForgotPasswordRequestInterface,
  ProviderInfoInterface
} from './http/apiservicemodel';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserInterface | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  private providerInfoSubject = new BehaviorSubject<ProviderInfoInterface | null>(null);
  public providerInfo$ = this.providerInfoSubject.asObservable();

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  private sessionExpiryTimer: any = null;

  constructor(
    private apiService: Apiservice,
    private router: Router,
    private tokenService: TokenService,
    private oauth2Service: OAuth2Service,
    private logger: LoggerService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    // Carrega auth status imediatamente se estiver no browser
    if (isPlatformBrowser(this.platformId)) {
      this.checkAuthStatus();
    }
  }

  /**
   * Verifica se o usuário está autenticado ao inicializar o service
   */
  private checkAuthStatus(): void {
    // Check if we're in browser environment more robustly
    const isBrowser = isPlatformBrowser(this.platformId) && typeof window !== 'undefined' && typeof localStorage !== 'undefined';
    
    if (!isBrowser) {
      return;
    }

    const token = this.tokenService.getAccessToken();
    const user = localStorage.getItem('user');
    const providerInfo = localStorage.getItem('provider_info');

    if (token && user && !this.tokenService.isTokenExpired()) {
      try {
        const userData = JSON.parse(user);
        this.currentUserSubject.next(userData);
        this.isAuthenticatedSubject.next(true);

        if (providerInfo) {
          const providerData = JSON.parse(providerInfo);
          const normalizedProvider = {
            ...providerData,
            is_verified: providerData?.is_verified === true || providerData?.is_verified === 1 || providerData?.is_verified === '1',
            status: providerData?.status || 'Pending'
          } as ProviderInfoInterface;
          this.providerInfoSubject.next(normalizedProvider);
        }

        // Agendar refresh automático
        this.scheduleTokenRefresh();
      } catch (error) {
        this.clearAuthData();
      }
    } else if (token && this.tokenService.isTokenExpired()) {
      // Token expirado, limpa os dados
      this.clearAuthData();
    }
  }

  /**
   * Registra um novo usuário (turistas, provedores e admins)
   */
  register(userData: RegisterRequestInterface): Observable<AuthResponseInterface> {
    // Mantém o tipo de usuário original (tourist, provider, etc.)
    const registrationData: RegisterRequestInterface = {
      ...userData
    };

    return this.apiService.register(registrationData).pipe(
      tap(response => {
        this.handleAuthResponse(response);
        // Usar setTimeout para garantir que localStorage seja atualizado antes do redirecionamento
        setTimeout(() => {
          // Redireciona apenas para turistas e admins (provedores são tratados pelo Provider Wizard)
          if (response.user.user_type === 'tourist' || response.user.user_type === 'admin') {
            this.router.navigate(['/profile']);
          }
          // Provedores não são redirecionados aqui - o Provider Wizard cuida disso
        }, 100);
      })
    );
  }

  /**
   * Registra um novo provedor (mantém o user_type original)
   */
  registerProvider(userData: RegisterRequestInterface): Observable<AuthResponseInterface> {
    // Mantém o user_type original (provider)
    return this.apiService.register(userData).pipe(
      tap(response => {
        this.handleAuthResponse(response);
        // Não redireciona automaticamente - deixa o componente decidir
        // O redirecionamento será feito após a criação do provider no wizard
      })
    );
  }

  /**
   * Processa callback OAuth2 e decide entre login ou registro
   * NOVO: Agora envia apenas o código de autorização para o backend
   */
  handleOAuth2Callback(): Observable<AuthResponseInterface> {
    this.logger.log('AuthService.handleOAuth2Callback - Iniciando processamento do callback');
    
    // Processa o callback e extrai o código
    return from(this.oauth2Service.processCallback()).pipe(
      switchMap((code: string | null) => {
        if (!code) {
          this.logger.error('AuthService.handleOAuth2Callback - Código não encontrado');
          return throwError(() => new Error('Código de autorização não encontrado'));
        }

        this.logger.log('AuthService.handleOAuth2Callback - Código obtido:', code);

        // Verifica se é modo de registro
        let isRegisterMode = false;
        this.oauth2Service.isRegisterMode$.pipe(take(1)).subscribe((mode: boolean) => {
          isRegisterMode = mode;
        });

        this.logger.log('AuthService.handleOAuth2Callback - Modo de registro:', isRegisterMode);

        if (isRegisterMode) {
          this.logger.log('AuthService.handleOAuth2Callback - Executando registro OAuth2');
          return this.apiService.registerWithOAuth2(code);
        } else {
          this.logger.log('AuthService.handleOAuth2Callback - Executando login OAuth2');
          return this.apiService.loginWithOAuth2(code);
        }
      }),
      tap((response: AuthResponseInterface) => {
        this.logger.log('AuthService.handleOAuth2Callback - Resposta recebida:', response);
        
        // Validar a resposta antes de processar
        if (!response.access_token) {
          this.logger.error('AuthService.handleOAuth2Callback - ERRO: access_token não encontrado na resposta');
          throw new Error('Token de acesso não recebido do servidor');
        }
        
        if (!response.refresh_token) {
          this.logger.error('AuthService.handleOAuth2Callback - ERRO: refresh_token não encontrado na resposta');
          throw new Error('Token de refresh não recebido do servidor');
        }
        
        if (!response.user || !response.user.id) {
          this.logger.error('AuthService.handleOAuth2Callback - ERRO: Dados do usuário incompletos');
          throw new Error('Dados do usuário não recebidos do servidor');
        }
        
        this.logger.log('AuthService.handleOAuth2Callback - Validação da resposta OK');
        this.logger.log('AuthService.handleOAuth2Callback - User ID:', response.user.id);
        this.logger.log('AuthService.handleOAuth2Callback - User Type:', response.user.user_type);
        
        // IMPORTANTE: Processar a resposta de autenticação ANTES de redirecionar
        this.handleAuthResponse(response);
        
        // Usar setTimeout para garantir que o localStorage foi atualizado
        // antes de qualquer navegação que possa disparar chamadas à API
        setTimeout(() => {
          this.logger.log('AuthService.handleOAuth2Callback - Redirecionando após salvamento completo');
          this.redirectAfterLogin();
        }, 100);
      }),
      catchError((error) => {
        this.logger.error('AuthService.handleOAuth2Callback - Erro capturado:', error);
        return throwError(() => error);
      })
    );
  }



  /**
   * Faz login do usuário
   */
  login(credentials: LoginRequestInterface): Observable<AuthResponseInterface> {
    return this.apiService.login(credentials).pipe(
      tap(response => {
        this.handleAuthResponse(response);
        // Usar setTimeout para garantir consistência com OAuth2
        setTimeout(() => {
          this.redirectAfterLogin();
        }, 100);
      })
    );
  }

  /**
   * Redireciona o usuário após login baseado em seu tipo e status
   */
  private redirectAfterLogin(): void {
    // Super admins vão para o dashboard
    if (this.isSuperAdmin()) {
      this.router.navigate(['/sadashboard']);
      return;
    }

    // Provedores (verificados ou não) vão para o dashboard principal
    // A página do dashboard já trata estados de pendência/associação
    if (this.isProvider()) {
      this.router.navigate(['/sadashboard']);
      return;
    }

    // Todos os outros (turistas, admins comuns, provedores sem associação) vão para /profile
    this.router.navigate(['/profile']);
  }

  /**
   * Faz logout do usuário
   */
  logout(): void {
    this.cancelTokenRefresh();
    this.clearAuthData();
    this.router.navigate(['/']);
    this.apiService.logout().subscribe({});
  }

  scheduleTokenRefresh(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.cancelTokenRefresh();

    const timeUntilExpiry = this.tokenService.getTimeUntilExpiration() * 1000;
    if (timeUntilExpiry <= 0) return;

    this.sessionExpiryTimer = setTimeout(() => {
      if (!this.isAuthenticated()) return;

      this.refreshAccessToken().subscribe({
        next: () => {
          // Refresh bem sucedido — reagendar
          this.scheduleTokenRefresh();
        },
        error: () => {
          // Refresh falhou — redirecionar com feedback
          this.clearAuthData();
          this.router.navigate(['/'], { queryParams: { sessionExpired: 'true' } });
        }
      });
    }, timeUntilExpiry);
  }

  cancelTokenRefresh(): void {
    if (this.sessionExpiryTimer) {
      clearTimeout(this.sessionExpiryTimer);
      this.sessionExpiryTimer = null;
    }
  }

  /**
   * Obtém o usuário atual com informações completas (incluindo provider_info)
   */
  getCurrentUser(): Observable<AuthResponseWithProviderInterface> {
    return this.apiService.getCurrentUser().pipe(
      tap(response => {
        this.currentUserSubject.next(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
        
        // Usar os dados reais da resposta em vez de dados hardcoded
        if (response.provider_info) {
          const normalizedProvider = {
            ...response.provider_info,
            is_verified: Boolean(response.provider_info?.is_verified),
            status: response.provider_info?.status || 'Pending'
          } as ProviderInfoInterface;
          localStorage.setItem('provider_info', JSON.stringify(normalizedProvider));
          this.providerInfoSubject.next(normalizedProvider);
        } else {
          localStorage.removeItem('provider_info');
          this.providerInfoSubject.next(null);
        }
      })
    );
  }

  /**
   * Altera a senha do usuário
   */
  changePassword(passwordData: ChangePasswordRequestInterface): Observable<void> {
    return this.apiService.changePassword(passwordData);
  }

  /**
   * Solicita redefinição de senha
   */
  forgotPassword(emailData: ForgotPasswordRequestInterface): Observable<void> {
    return this.apiService.forgotPassword(emailData);
  }

  /**
   * Verifica se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  /**
   * Obtém o usuário atual (síncrono)
   */
  getCurrentUserValue(): UserInterface | null {
    return this.currentUserSubject.value;
  }

  /**
   * Verifica se o usuário é um provedor
   */
  isProvider(): boolean {
    const user = this.getCurrentUserValue();
    return user?.user_type === 'provider';
  }

  /**
   * Verifica se o usuário é um turista
   */
  isTourist(): boolean {
    const user = this.getCurrentUserValue();
    return user?.user_type === 'tourist';
  }

  /**
   * Verifica se o usuário é um admin
   */
  isAdmin(): boolean {
    const user = this.getCurrentUserValue();
    return user?.user_type === 'admin';
  }

  /**
   * Verifica se o usuário é um super admin
   */
  isSuperAdmin(): boolean {
    const user = this.getCurrentUserValue();
    return user?.user_type === 'admin' && (user as UserInterface & { role?: string })?.role === 'super_admin';
  }

  /**
   * Verifica se o usuário tem provider associado
   */
  hasProviderAssociated(): boolean {
    // Primeiro verifica se já está carregado na memória
    if (this.providerInfoSubject.value !== null) {
      return true;
    }

    // Se não está carregado, tenta carregar do localStorage (fallback para guards)
    if (isPlatformBrowser(this.platformId) && typeof localStorage !== 'undefined') {
      const providerInfo = localStorage.getItem('provider_info');
      if (providerInfo) {
        try {
          const providerData = JSON.parse(providerInfo);
          if (providerData && Object.keys(providerData).length > 0) {
            // Carrega os dados se não estavam carregados
            const normalizedProvider = {
              ...providerData,
              is_verified: providerData?.is_verified === true || providerData?.is_verified === 1 || providerData?.is_verified === '1',
              status: providerData?.status || 'Pending'
            } as ProviderInfoInterface;
            this.providerInfoSubject.next(normalizedProvider);
            return true;
          }
        } catch (error) {
          // Se há erro no JSON, remove os dados inválidos
          localStorage.removeItem('provider_info');
        }
      }
    }

    return false;
  }

  /**
   * Verifica se o usuário é provider E tem provider associado
   */
  isProviderWithAssociation(): boolean {
    return this.isProvider() && this.hasProviderAssociated();
  }

  /**
   * Verifica se o provider está verificado/aprovado
   */
  isProviderVerified(): boolean {
      const providerInfo = this.getProviderInfoValue();
      return providerInfo?.status === 'Approved';
    }

  /**
   * Verifica se o usuário é provider verificado (com acesso completo)
   */
  isVerifiedProvider(): boolean {
    return this.isProviderWithAssociation() && this.isProviderVerified();
  }

  /**
   * Verifica se o usuário é provider pendente (aguardando aprovação)
   */
  isPendingProvider(): boolean {
    return this.isProviderWithAssociation() && !this.isProviderVerified();
  }

  /**
   * Obtém o token de acesso
   */
  getAccessToken(): string | null {
    return this.tokenService.getAccessToken();
  }

  /**
   * Obtém o token de refresh
   */
  getRefreshToken(): string | null {
    return this.tokenService.getRefreshToken();
  }

  /**
   * Atualiza o token de acesso usando o refresh token
   */
  refreshAccessToken(): Observable<AuthResponseInterface> {
    const refreshToken = this.getRefreshToken();
    const userId = this.getCurrentUserValue()?.id;

    if (!refreshToken || !userId) {
      return throwError(() => new Error('No refresh token or user ID available'));
    }

    return this.apiService.refreshToken({
      userId: userId,
      refreshToken: refreshToken
    }).pipe(
      tap(response => {
        this.handleAuthResponse(response);
      })
    );
  }

  /**
   * Verifica se o token está próximo de expirar
   */
  isTokenExpiringSoon(): boolean {
    return this.tokenService.isTokenExpiringSoon();
  }

  /**
   * Verifica se o token está expirado
   */
  isTokenExpired(): boolean {
    return this.tokenService.isTokenExpired();
  }

  /**
   * Obtém as informações do provider (síncrono)
   */
  getProviderInfoValue(): ProviderInfoInterface | null {
    return this.providerInfoSubject.value;
  }

  /**
   * Obtém as informações do provider (observable)
   */
  getProviderInfo(): Observable<ProviderInfoInterface | null> {
    return this.providerInfo$;
  }

  /**
   * Atualiza as informações do provider no estado local
   */
  updateProviderInfo(providerInfo: ProviderInfoInterface): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const normalizedProvider: ProviderInfoInterface = {
      id: providerInfo.id,
      company_name: providerInfo.company_name,
      description: providerInfo.description,
      address: providerInfo.address,
      company_phone: providerInfo.company_phone,
      company_email: providerInfo.company_email,
      tax_number: providerInfo.tax_number,
      website: providerInfo.website || '',
      logo: providerInfo.logo || '',
      is_verified: Boolean(providerInfo?.is_verified),
      role: 'Manager', // Default role for new providers
      status: providerInfo?.status || 'Pending'
    };
    
    localStorage.setItem('provider_info', JSON.stringify(normalizedProvider));
    this.providerInfoSubject.next(normalizedProvider);
  }

  /**
   * Processa a resposta de autenticação
   */
  public handleAuthResponse(response: AuthResponseInterface | AuthResponseWithProviderInterface, shouldRedirect: boolean = false): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.logger.log('AuthService.handleAuthResponse - Iniciando processamento da resposta');
    this.logger.log('AuthService.handleAuthResponse - User ID:', response.user.id);
    this.logger.log('AuthService.handleAuthResponse - Access Token presente:', !!response.access_token);
    this.logger.log('AuthService.handleAuthResponse - Refresh Token presente:', !!response.refresh_token);

    // Usa o TokenService para gerenciar os tokens
    this.tokenService.setAccessToken(response.access_token);
    this.tokenService.setRefreshToken(response.refresh_token);
    this.tokenService.setUserId(response.user.id);
    
    // Verificar se foi salvo corretamente
    const savedUserId = this.tokenService.getUserId();
    const savedAccessToken = this.tokenService.getAccessToken();
    const savedRefreshToken = this.tokenService.getRefreshToken();
    
    this.logger.log('AuthService.handleAuthResponse - User ID salvo:', savedUserId);
    this.logger.log('AuthService.handleAuthResponse - Access Token salvo:', !!savedAccessToken);
    this.logger.log('AuthService.handleAuthResponse - Refresh Token salvo:', !!savedRefreshToken);
    
    localStorage.setItem('user', JSON.stringify(response.user));

    this.currentUserSubject.next(response.user);
    this.isAuthenticatedSubject.next(true);

    // Handle provider info if available
    if ('provider_info' in response && response.provider_info) {
      const normalizedProvider = {
        ...response.provider_info,
        is_verified: Boolean((response.provider_info as any)?.is_verified),
        status: (response.provider_info as any)?.status || 'Pending'
      } as ProviderInfoInterface;
      localStorage.setItem('provider_info', JSON.stringify(normalizedProvider));
      this.providerInfoSubject.next(normalizedProvider);
    }

    this.logger.log('AuthService.handleAuthResponse - Processamento concluído');

    // Agendar refresh automático do token
    this.scheduleTokenRefresh();

    // Redirecionar se solicitado
    if (shouldRedirect) {
      this.redirectAfterLogin();
    }
  }

  /**
   * Updates the current user data in the service
   */
  public updateCurrentUser(user: UserInterface): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    localStorage.setItem('user', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  /**
   * Limpa todos os dados de autenticação
   */
  private clearAuthData(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Usa o TokenService para limpar os tokens
    this.tokenService.clearTokens();
    
    localStorage.removeItem('user');
    localStorage.removeItem('provider_info');

    this.currentUserSubject.next(null);
    this.providerInfoSubject.next(null);
    this.isAuthenticatedSubject.next(false);
  }
}





